import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Input,
  Form,
  Row,
  Col,
  message,
  Breadcrumb,
  Upload,
  Switch,
} from "antd";
import { Controller, useForm } from "react-hook-form";
import {
  createProductImportRequest,
  getProductByIdRequest,
  updateProductRequest,
} from "../../../api/requests/product";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { mutationOnErrorHandler } from "../../../utils/mutationUtils";
import { useEffect, useState } from "react";
import WelcomeTopbar from "../../../components/common/header/WelcomeTopbar";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { doDownload } from "../../../utils/download";

const updateProductSchemaResolver = yupResolver(
  yup.object().shape({
    name: yup.string(),
    description: yup.string(),
    customAttributes: yup.object().shape({
      type: yup.string(),
      size: yup
        .number()
        .positive("Size must be positive")
        .integer("Size must be an integer"),
      color: yup.string(),
    }),
    active: yup.boolean(),
  }),
);

function UpdateProductPage() {
  const params = useParams();
  const { id } = params;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [fileLoading, setFileLoading] = useState(false);

  const { data: productDetails } = useQuery({
    queryKey: ["product", id],
    queryFn: async () => {
      const res = await getProductByIdRequest({
        id,
        params: {},
      });
      return res.data?.data;
    },
  });

  const { mutateAsync: updateProduct, isPending: updatingProduct } =
    useMutation({
      mutationFn: async (data) => {
        const res = await updateProductRequest({ id, data });
        return res.data;
      },
      mutationKey: ["product", id],
      onSuccess: (res) => {
        queryClient.invalidateQueries(["product"]);
        const successMessage = res?.message;
        if (successMessage) {
          message.success(successMessage);
        }
        navigate(-1);
      },
      onError: (error) => {
        mutationOnErrorHandler({ error, message });
      },
    });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: updateProductSchemaResolver,
  });

  async function onSubmit(data) {
    // console.log(data);
    const res = await updateProduct(data);
    // console.log("res----->", res);
  }

  const handleUploadChange = ({ file }) => {
    if (file.status === "done") {
      setFileLoading(false);
    } else if (file.status === "uploading") {
      setFileLoading(true);
    }
  };

  const { mutateAsync: createProductImport, isPending: updatingProductImport } =
    useMutation({
      mutationFn: async ({ productId, data }) => {
        const res = await createProductImportRequest({ productId, data });
        return res.data;
      },
      mutationKey: [`product/${id}/import`],
      onSuccess: (res) => {
        queryClient.invalidateQueries(["product"]);
        const successMessage = res?.message;
        const { errorCount = 0, successCount = 0 } = res?.data || {};
        if (successMessage) {
          message.success(
            `${successMessage}
            success count - ${successCount}
            error count - ${errorCount}`,
          );
        }
        navigate(-1);
      },
      onError: (error) => {
        mutationOnErrorHandler({ error, message });
      },
    });

  const handleFileUploadCustomRequest = async ({
    file,
    onSuccess,
    onError,
  }) => {
    const formData = new FormData();
    formData.append("file", file);
    await createProductImport(
      { productId: id, data: formData },
      {
        onSuccess: () => onSuccess(),
        onError: (error) => onError(error),
      },
    );
  };

  useEffect(() => {
    if (productDetails) {
      const {
        name,
        description,
        customAttributes = {},
        active,
        activeAssetCount,
        decommissionedAssetCount,
        weight,
      } = productDetails;
      const { type, size, color } = customAttributes;

      reset({
        name,
        description,
        customAttributes: {
          type,
          size,
          color,
        },
        active,
        activeAssetCount,
        decommissionedAssetCount,
        weight,
      });
    }
  }, [productDetails, reset]);

  return (
    <div className="flex flex-col">
      <WelcomeTopbar />
      <div className="flex flex-col gap-5 pb-6">
        <div className="flex flex-col gap-6">
          <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
            <div className="flex items-center justify-between rounded-t-xl bg-[#D1DFFF] px-6 py-3">
              <Breadcrumb
                items={[
                  {
                    title: <Link to={"/setup/products"}>Products</Link>,
                  },
                  {
                    title: <div className="text-lg">Update</div>,
                  },
                ]}
              />
              <Button
                type="primary"
                htmlType="submit"
                className="flex items-center justify-center"
                loading={updatingProduct}
              >
                Update
              </Button>
            </div>
            <div className="px-6 py-6">
              <Row gutter={18}>
                <Col span={12}>
                  <Form.Item
                    label="Name"
                    name="name"
                    validateStatus={errors.name ? "error" : ""}
                    help={errors.name && errors.name.message}
                  >
                    <Controller
                      control={control}
                      name="name"
                      render={({ field }) => (
                        <Input
                          {...field}
                          placeholder="Name"
                          autoComplete="product-name"
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Description"
                    name="description"
                    validateStatus={errors.description ? "error" : ""}
                    help={errors.description && errors.description.message}
                  >
                    <Controller
                      control={control}
                      name="description"
                      render={({ field }) => (
                        <Input {...field} placeholder="Description" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Type"
                    name="customAttributes.type"
                    validateStatus={
                      errors.customAttributes?.type ? "error" : ""
                    }
                    help={errors.customAttributes?.type?.message}
                  >
                    <Controller
                      control={control}
                      name="customAttributes.type"
                      render={({ field }) => (
                        <Input {...field} placeholder="Type" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Size"
                    name="customAttributes.size"
                    validateStatus={
                      errors.customAttributes?.size ? "error" : ""
                    }
                    help={errors.customAttributes?.size?.message}
                  >
                    <Controller
                      control={control}
                      name="customAttributes.size"
                      render={({ field }) => (
                        <Input {...field} placeholder="Size" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Weight (lb)"
                    name="weight"
                    validateStatus={errors.weight ? "error" : ""}
                    help={errors?.weight?.message}
                  >
                    <Controller
                      control={control}
                      name="weight"
                      render={({ field }) => (
                        <Input
                          {...field}
                          placeholder="Weight"
                          type="number"
                          min={0}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Color"
                    name="customAttributes.color"
                    validateStatus={
                      errors.customAttributes?.color ? "error" : ""
                    }
                    help={errors.customAttributes?.color?.message}
                  >
                    <Controller
                      control={control}
                      name="customAttributes.color"
                      render={({ field }) => (
                        <Input {...field} placeholder="Color" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Available" name="activeAssetCount">
                    <Controller
                      control={control}
                      name="activeAssetCount"
                      render={({ field }) => (
                        <Input {...field} disabled={true} />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="Decommissioned"
                    name="decommissionedAssetCount"
                  >
                    <Controller
                      control={control}
                      name="decommissionedAssetCount"
                      render={({ field }) => (
                        <Input {...field} disabled={true} />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Active"
                    name="active"
                    validateStatus={errors.active ? "error" : ""}
                    help={errors.active && errors.active.message}
                  >
                    <Controller
                      control={control}
                      name="active"
                      render={({ field }) => <Switch {...field} />}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <div className="flex items-center gap-4">
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={() => {
                        doDownload(
                          "/assets/csv/productassets.csv",
                          "productassets.csv",
                        );
                      }}
                    >
                      Download Template
                    </Button>
                    <Upload
                      customRequest={handleFileUploadCustomRequest}
                      showUploadList={false}
                      accept=".csv"
                      onChange={handleUploadChange}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        // className="bg-blue-500 text-white"
                        loading={fileLoading}
                      >
                        Add Asset
                      </Button>
                    </Upload>
                  </div>
                </Col>
              </Row>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default UpdateProductPage;
